import type { ResponderThemeInput } from '@/shared-responder-v2-shell';
import { resolveThemeFontFamily } from '@/shared-utilities';

export interface ResponderOutroProjectInput {
  readonly isThankyouEnabled?: boolean | null;
  readonly thankyouText?: string | null;
  readonly thankyouDescription?: string | null;
  readonly completionRedirectUrl?: string | null;
}

export const EMPTY_RESPONDER_OUTRO_INPUT: ResponderOutroProjectInput = {};

export interface ResponderOutroContent {
  readonly title: string | null;
  readonly description: string | null;
}

/**
 * Only the fields the responder's page chrome reads. `SurveyProject` satisfies
 * this structurally, so callers pass the project directly — but narrowing the
 * input keeps these helpers testable without constructing a whole generated
 * GraphQL type.
 */
export interface ResponderChromeInput extends ResponderOutroProjectInput {
  readonly publicTitle?: string | null;
  readonly orgFont?: string | null;
  readonly title?: string | null;
  readonly estimatedCompletionTime?: number | null;
  readonly intro?: {
    readonly title?: string | null;
    readonly subTitle?: string | null;
  } | null;
  readonly introVideo?: { readonly url?: string | null } | null;
  readonly themeSettings?: {
    readonly bgColor?: string | null;
    readonly bgImage?: string | null;
    readonly font?: string | null;
    readonly titleFont?: string | null;
    readonly titleFontWeight?: 'LIGHT' | 'REGULAR' | 'BOLD' | null;
    readonly primaryColor?: string | null;
    readonly transparency?: number | null;
    readonly mode?: 'LIGHT' | 'DARK' | 'AUTO' | null;
    readonly overlayOpacity?: number | null;
  } | null;
}

/** Copy and media for the pre-survey intro screen. */
export interface ResponderIntroContent {
  readonly title: string;
  readonly body: string;
  readonly videoUrl: string | null;
  readonly estimatedCompletionTimeInSeconds: number | null;
}

export const EMPTY_RESPONDER_THEME: ResponderThemeInput = {
  primaryColor: null,
  backgroundColor: null,
  backgroundImageUrl: null,
  fontFamily: null,
  titleFontFamily: null,
  titleFontWeight: null,
  surfaceOpacity: null,
  mode: 'light',
  overlayOpacity: null,
};

/**
 * The intro screen's content, or `null` when the project has no intro.
 *
 * A project can opt into the intro with either V2 intro copy or its persisted
 * project-level intro video. Both being absent is the disabled state. Returning
 * `null` here is therefore the signal to skip the Intro phase, not merely to
 * render it empty.
 *
 * Empty copy with an intro present is deliberately still an intro: presence is
 * the author's intent, and the screen can carry the video and Start control
 * with no headline.
 */
export function resolveResponderIntro(
  project: ResponderChromeInput | null
): ResponderIntroContent | null {
  const intro = project?.intro;
  const videoUrl = project?.introVideo?.url ?? null;

  if (!intro && videoUrl === null) return null;

  return {
    title: intro?.title?.trim() ?? '',
    body: intro?.subTitle?.trim() ?? '',
    videoUrl,
    estimatedCompletionTimeInSeconds: project?.estimatedCompletionTime ?? null,
  };
}

function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

export function isResponderOutroEnabled(
  project: Pick<
    ResponderOutroProjectInput,
    'isThankyouEnabled' | 'thankyouText'
  >
): boolean {
  return project.isThankyouEnabled ?? trimToNull(project.thankyouText) !== null;
}

export function resolveResponderOutroContent(
  args: ResponderOutroProjectInput & {
    readonly backendThankYouText: string | null | undefined;
  }
): ResponderOutroContent {
  if (!isResponderOutroEnabled(args)) {
    return { title: null, description: null };
  }

  return {
    title:
      trimToNull(args.backendThankYouText) ?? trimToNull(args.thankyouText),
    description: trimToNull(args.thankyouDescription),
  };
}

const PERSISTED_PERCENTAGE_MAX = 100;

/**
 * Maps the project's persisted theme onto the shell's theme input, which the
 * shell turns into the shared `--theysaid-*` custom properties. Every field stays
 * nullable: null means "no author override", so the shell falls back to its own
 * default rather than painting an empty value.
 *
 * `transparency` and `overlayOpacity` are both persisted 0–100 while the
 * shell's `surfaceOpacity`/`overlayOpacity` are CSS alphas, so the conversion
 * happens here for both — the same `/100` the legacy responder applies in
 * `createThemeSettingsStyle`. Without it the shell's clamp pins every authored
 * value to 1 and the setting silently does nothing.
 *
 * `mode` collapses the persisted `LIGHT | DARK | AUTO` setting onto the
 * concrete `'light' | 'dark'` the surface resolvers switch on — `AUTO` and an
 * absent setting both resolve to `'light'`, matching the Figma default an
 * unthemed project already renders, since the responder has no client-side
 * OS-preference read to resolve `AUTO` against the way Builder V2 Design does.
 */
export function resolveResponderTheme(
  project: ResponderChromeInput | null
): ResponderThemeInput {
  const settings = project?.themeSettings;
  const fontFamily = resolveThemeFontFamily(settings?.font, project?.orgFont);

  if (!settings) return { ...EMPTY_RESPONDER_THEME, fontFamily };

  return {
    primaryColor: settings.primaryColor ?? null,
    backgroundColor: settings.bgColor ?? null,
    backgroundImageUrl: settings.bgImage ?? null,
    fontFamily,
    titleFontFamily: settings.titleFont ?? null,
    titleFontWeight: settings.titleFontWeight ?? null,
    surfaceOpacity:
      settings.transparency == null
        ? null
        : settings.transparency / PERSISTED_PERCENTAGE_MAX,
    mode: settings.mode === 'DARK' ? 'dark' : 'light',
    overlayOpacity:
      settings.overlayOpacity == null
        ? null
        : settings.overlayOpacity / PERSISTED_PERCENTAGE_MAX,
  };
}
